import { SELF_SIGNUP_PLAN_COMPANIES } from '@revelio/core/self-signup-plan';
import type { CustomRoleTaxonomySelection } from '@revelio/data-access';
import {
  GENERATED_ACTIVITY_DEFAULTS_VERSION,
  GENERATED_DEFAULT_CUSTOM_ACTIVITY_DEFAULT,
} from '@revelio/data-access/activity-defaults';
import {
  GENERATED_DEFAULT_CUSTOM_ROLE_DEFAULT,
  GENERATED_ROLE_DEFAULTS_VERSION,
} from '@revelio/data-access/role-defaults';
import {
  GENERATED_DEFAULT_CUSTOM_SKILL_DEFAULT,
  GENERATED_SKILL_DEFAULTS_VERSION,
} from '@revelio/data-access/skill-defaults';
import { MAX_PRIMARY_ENTITY_SELECTIONS } from '@revelio/iso-utility';

import {
  PreferenceScope,
  UserPreferenceKey,
} from '../../../../shared/user-preferences/types';
import { deriveEffectiveTaxonomiesForScope } from '../../../../shared/user-preferences/utils/derive-effective-taxonomies';
import { resolveGeographyPreference } from '../../../../shared/user-preferences/utils/geography-preference';
import { PreferenceBrowserStorage } from '../../../../shared/user-preferences/utils/preference-session-storage';
import { getPreferenceScope } from '../../../../shared/user-preferences/utils/preferences';
import { reconcileVersionedDefaults } from '../../../../shared/user-preferences/utils/reconcileVersionedDefaults';
import type { CustomActivityTaxonomySelection } from '../definitions/activityTaxonomy';
import type { CustomSkillTaxonomySelection } from '../definitions/skillTaxonomy';
import { ALL_FILTER_DEFINITIONS } from '../engine/filterRegistry';
import {
  type Filter,
  FilterId,
  FilterType,
  type PrimaryFilterId,
  type SingleValueFilter,
  type TreeFilter,
} from '../types/filters';
import type { TaxonomyConfig } from '../types/taxonomy';
import { getActivityDefaultSelectionsForTaxonomy } from '../utils/getActivityDefaultSelectionsForTaxonomy';
import {
  DEFAULT_PRIMARY_GEOGRAPHY,
  DEFAULT_PRIMARY_GEOGRAPHY_ADMINISTRATIVE,
  getGeographyDefaultSelectionsForTaxonomy,
  selectionsForGeographyTaxonomy,
} from '../utils/getGeographyDefaultSelectionsForTaxonomy';
import { getRoleDefaultSelectionsForTaxonomy } from '../utils/getRoleDefaultSelectionsForTaxonomy';
import { getSkillDefaultSelectionsForTaxonomy } from '../utils/getSkillDefaultSelectionsForTaxonomy';
import {
  enforceMaxSelections,
  pickFirstWhenMatchingPreset,
} from '../utils/primaryFilterUtils';

export { DEFAULT_PRIMARY_GEOGRAPHY, DEFAULT_PRIMARY_GEOGRAPHY_ADMINISTRATIVE };

export const DEFAULT_SKILL_TAXONOMY_ID = 0;
export const DEFAULT_ACTIVITY_TAXONOMY_ID = 0;

export const DEFAULT_FILTERS: Filter[] = Object.values(
  ALL_FILTER_DEFINITIONS
).sort((a, b) => a.name.localeCompare(b.name));

/**
 * The product's default primary companies, which are also the companies a
 * self-signup account may analyse: one list, in `@revelio/core/self-signup-plan`,
 * so the picker's shortlist and the entitlement cannot drift.
 */
export const DEFAULT_PRIMARY_COMPANIES: TreeFilter['selections'] =
  SELF_SIGNUP_PLAN_COMPANIES.map(
    ({ selectionId, name }): TreeFilter['selections'][number] => ({
      id: selectionId,
      name,
      levelId: 'company',
    })
  );

/**
 * The companies a self-signup trial user may analyse, as bare rcids.
 *
 * The company picker offers the defaults as its shortlist and refuses a submit
 * holding anything else (see `trial/trialCompanyAllowList.ts`). The company
 * selection list is NOT filtered per user — measured on a live self-signup
 * trial session, the RICS tree returns every company (~57k, Tesla included) —
 * so it cannot serve as the allow-list, and go-api does not filter by plan
 * either, which is why dash-meta's server-side reads check the same list. When
 * go-api starts filtering by the client's `company_lists`, this, its consumers
 * in `trial/trialCompanyAllowList.ts` and `ai-search/restrictCompaniesForTrial.ts`,
 * and that read check should all go away.
 */
export const TRIAL_ALLOWED_COMPANY_RCIDS: ReadonlySet<string> = new Set(
  SELF_SIGNUP_PLAN_COMPANIES.map((company) => String(company.rcid))
);

export const DEFAULT_PRIMARY_INDUSTRIES: TreeFilter['selections'] = [
  { id: '8_256', name: 'Enterprise Software Solutions', levelId: 'rics_k400' },
  { id: '8_250', name: 'Cybersecurity', levelId: 'rics_k400' },
  { id: '4_148', name: 'Financial Services', levelId: 'rics_k400' },
  { id: '8_270', name: 'IT Services', levelId: 'rics_k400' },
  { id: '8_57', name: 'Digital Services and E-Commerce', levelId: 'rics_k400' },
];

export const DEFAULT_PRIMARY_CUSTOM_ROLE: TreeFilter['selections'] =
  GENERATED_DEFAULT_CUSTOM_ROLE_DEFAULT;

export const DEFAULT_PRIMARY_CUSTOM_SKILL: TreeFilter['selections'] =
  GENERATED_DEFAULT_CUSTOM_SKILL_DEFAULT;

export const DEFAULT_PRIMARY_ACTIVITY: TreeFilter['selections'] =
  GENERATED_DEFAULT_CUSTOM_ACTIVITY_DEFAULT;

const SUPERSEDED_DEFAULT_CUSTOM_ROLE_IDS = [
  '2_117',
  '4_53',
  '7_68',
  '10_63',
  '11_127',
];

const isSupersededRoleDefault = (saved: TreeFilter['selections']): boolean =>
  saved.length === SUPERSEDED_DEFAULT_CUSTOM_ROLE_IDS.length &&
  saved.every(
    (selection, i) => selection.id === SUPERSEDED_DEFAULT_CUSTOM_ROLE_IDS[i]
  );

export type DefaultSelectionOverride = {
  [FilterId.COMPANY]?: TreeFilter['selections'];
  [FilterId.INDUSTRY]?: TreeFilter['selections'];
  [FilterId.GEOGRAPHY]?: TreeFilter['selections'];
  [FilterId.ROLE]?: TreeFilter['selections'];
  [FilterId.CUSTOM_SKILL]?: TreeFilter['selections'];
  [FilterId.ACTIVITY]?: TreeFilter['selections'];
};

const resolvePrimaryDefaultSelections = (
  primaryFilterId: PrimaryFilterId,
  taxonomyConfig?: TaxonomyConfig,
  defaultSelections?: DefaultSelectionOverride
): TreeFilter['selections'] => {
  // Simplified entity selection seeds a single default entity; saved
  // preferences and explicit overrides are honored in full.
  const hardCodedDefaults = (selections: TreeFilter['selections']) =>
    selections.slice(0, 1);

  const scopeForFilter = getPreferenceScope({ primaryFilterId });
  const preferencesForScope = PreferenceBrowserStorage.get(scopeForFilter);
  const userDefaultSelections =
    preferencesForScope?.[UserPreferenceKey.DefaultFilters];
  const hasSavedSelections =
    !!userDefaultSelections && userDefaultSelections.length > 0;
  // Taxonomy choice is GLOBAL (derived per scope: adaptive → generic outside company).
  const globalPreferences = PreferenceBrowserStorage.get(
    PreferenceScope.GLOBAL
  );
  const globalTaxonomies = deriveEffectiveTaxonomiesForScope(
    globalPreferences[UserPreferenceKey.Taxonomies],
    scopeForFilter
  );
  const geographyTaxonomyId = resolveGeographyPreference(
    globalPreferences[UserPreferenceKey.Geography]
  ).taxonomyId;

  const rolePref = globalTaxonomies?.role;
  const roleTaxonomySelection: CustomRoleTaxonomySelection =
    rolePref && rolePref !== 'adaptive' && typeof rolePref === 'object'
      ? rolePref
      : { default: true };

  // The skill taxonomy preference is a numeric id ('adaptive' derives to the
  // generic 0 outside company scope — see deriveEffectiveTaxonomiesForScope).
  const skillPref = globalTaxonomies?.skill;
  const skillTaxonomyId =
    typeof skillPref === 'number' ? skillPref : DEFAULT_SKILL_TAXONOMY_ID;
  const activityPref = globalTaxonomies?.activity;
  const activityTaxonomyId =
    typeof activityPref === 'number'
      ? activityPref
      : DEFAULT_ACTIVITY_TAXONOMY_ID;

  if (primaryFilterId === FilterId.ROLE) {
    // Version-gate the saved role defaults: a taxonomy bump (saved stamp !=
    // live version) must not serve stale selections. When not reusable we fall
    // through to the taxonomy/override/hard-coded chain below — the override is
    // where FilterProvider injects the first-from-live fallback.
    const { source } = reconcileVersionedDefaults({
      hasSaved: hasSavedSelections,
      savedVersion: globalTaxonomies?.roleTaxonomyVersion,
      currentVersion: taxonomyConfig?.roleTaxonomyVersion,
      bakedVersion: GENERATED_ROLE_DEFAULTS_VERSION,
    });
    if (
      source === 'saved' &&
      userDefaultSelections &&
      !isSupersededRoleDefault(userDefaultSelections)
    ) {
      return pickFirstWhenMatchingPreset(userDefaultSelections, [
        getRoleDefaultSelectionsForTaxonomy(roleTaxonomySelection),
        DEFAULT_PRIMARY_CUSTOM_ROLE,
      ]);
    }
  } else if (primaryFilterId === FilterId.CUSTOM_SKILL) {
    // Skill ids are taxonomy-relative, so a saved default captured under
    // another skill taxonomy version is not reusable.
    const { source } = reconcileVersionedDefaults({
      hasSaved: hasSavedSelections,
      savedVersion: globalTaxonomies?.skillTaxonomyVersion,
      currentVersion: taxonomyConfig?.skillTaxonomyVersion,
      bakedVersion: GENERATED_SKILL_DEFAULTS_VERSION,
    });
    if (source === 'saved' && userDefaultSelections) {
      return pickFirstWhenMatchingPreset(userDefaultSelections, [
        getSkillDefaultSelectionsForTaxonomy(skillTaxonomyId),
        DEFAULT_PRIMARY_CUSTOM_SKILL,
      ]);
    }
  } else if (primaryFilterId === FilterId.ACTIVITY) {
    // Same version gate again: activity ids are taxonomy-relative.
    const { source } = reconcileVersionedDefaults({
      hasSaved: hasSavedSelections,
      savedVersion: globalTaxonomies?.activityTaxonomyVersion,
      currentVersion: taxonomyConfig?.activityTaxonomyVersion,
      bakedVersion: GENERATED_ACTIVITY_DEFAULTS_VERSION,
    });
    if (source === 'saved' && userDefaultSelections) {
      return pickFirstWhenMatchingPreset(userDefaultSelections, [
        getActivityDefaultSelectionsForTaxonomy(activityTaxonomyId),
        DEFAULT_PRIMARY_ACTIVITY,
      ]);
    }
  } else if (primaryFilterId === FilterId.GEOGRAPHY) {
    const savedForTaxonomy = selectionsForGeographyTaxonomy(
      userDefaultSelections ?? [],
      geographyTaxonomyId
    );
    if (savedForTaxonomy.length > 0) {
      return pickFirstWhenMatchingPreset(savedForTaxonomy, [
        DEFAULT_PRIMARY_GEOGRAPHY,
        DEFAULT_PRIMARY_GEOGRAPHY_ADMINISTRATIVE,
      ]);
    }
  } else if (hasSavedSelections) {
    // COMPANY / INDUSTRY have no taxonomy — saved defaults win as-is.
    return pickFirstWhenMatchingPreset(userDefaultSelections, [
      DEFAULT_PRIMARY_COMPANIES,
      DEFAULT_PRIMARY_INDUSTRIES,
    ]);
  }

  switch (primaryFilterId) {
    case FilterId.COMPANY:
      return (
        defaultSelections?.[FilterId.COMPANY] ??
        hardCodedDefaults(DEFAULT_PRIMARY_COMPANIES)
      );
    case FilterId.INDUSTRY:
      return (
        defaultSelections?.[FilterId.INDUSTRY] ??
        hardCodedDefaults(DEFAULT_PRIMARY_INDUSTRIES)
      );
    case FilterId.GEOGRAPHY:
      return (
        defaultSelections?.[FilterId.GEOGRAPHY] ??
        hardCodedDefaults(
          getGeographyDefaultSelectionsForTaxonomy(geographyTaxonomyId)
        )
      );
    case FilterId.ROLE: {
      const taxonomyDefaults = getRoleDefaultSelectionsForTaxonomy(
        roleTaxonomySelection
      );

      return (
        defaultSelections?.[FilterId.ROLE] ??
        hardCodedDefaults(taxonomyDefaults ?? DEFAULT_PRIMARY_CUSTOM_ROLE)
      );
    }
    case FilterId.CUSTOM_SKILL: {
      const taxonomyDefaults =
        getSkillDefaultSelectionsForTaxonomy(skillTaxonomyId);

      return (
        defaultSelections?.[FilterId.CUSTOM_SKILL] ??
        hardCodedDefaults(
          taxonomyDefaults.length > 0
            ? taxonomyDefaults
            : DEFAULT_PRIMARY_CUSTOM_SKILL
        )
      );
    }
    case FilterId.ACTIVITY: {
      const taxonomyDefaults =
        getActivityDefaultSelectionsForTaxonomy(activityTaxonomyId);

      return (
        defaultSelections?.[FilterId.ACTIVITY] ??
        hardCodedDefaults(
          taxonomyDefaults.length > 0
            ? taxonomyDefaults
            : DEFAULT_PRIMARY_ACTIVITY
        )
      );
    }
    default:
      return [];
  }
};

export const createDefaultFilterMap = (
  primaryFilterIdToSet?: PrimaryFilterId,
  taxonomyConfig?: TaxonomyConfig,
  defaultSelections?: DefaultSelectionOverride
): Map<FilterId, Filter> => {
  const filterMap = new Map<FilterId, Filter>();

  Object.values(ALL_FILTER_DEFINITIONS).forEach((definition) => {
    // Skip MULTI filters - only include single versions by default
    // MULTI filters can still be added explicitly via templates
    if (definition.type === FilterType.MULTI) {
      return;
    }

    // The activity filters exist only where the activity filter is on —
    // seeding them unconditionally would leak them into every user's URL,
    // saved views, and persisted preferences — or when activity is the page's
    // unit of analysis, which needs its primary and taxonomy either way.
    if (
      (definition.id === FilterId.ACTIVITY ||
        definition.id === FilterId.ACTIVITY_TAXONOMY) &&
      taxonomyConfig?.activityTaxonomyEnabled !== true &&
      primaryFilterIdToSet !== FilterId.ACTIVITY
    ) {
      return;
    }

    const filterInstance: Filter = { ...definition };
    if (definition.id === FilterId.ROLE_TAXONOMY) {
      (filterInstance as SingleValueFilter<CustomRoleTaxonomySelection>).value =
        {
          default: true,
          version: taxonomyConfig?.roleTaxonomyVersion,
        };
    }
    if (definition.id === FilterId.SKILL_TAXONOMY) {
      (
        filterInstance as SingleValueFilter<CustomSkillTaxonomySelection>
      ).value = {
        taxonomyId: DEFAULT_SKILL_TAXONOMY_ID,
        version: taxonomyConfig?.skillTaxonomyVersion,
      };
    }
    if (definition.id === FilterId.ACTIVITY_TAXONOMY) {
      (
        filterInstance as SingleValueFilter<CustomActivityTaxonomySelection>
      ).value = {
        taxonomyId: DEFAULT_ACTIVITY_TAXONOMY_ID,
        version: taxonomyConfig?.activityTaxonomyVersion,
      };
    }

    filterMap.set(definition.id, filterInstance);
  });

  if (!primaryFilterIdToSet) return filterMap;

  const defaultSelectionsForPrimary = enforceMaxSelections(
    resolvePrimaryDefaultSelections(
      primaryFilterIdToSet,
      taxonomyConfig,
      defaultSelections
    ),
    MAX_PRIMARY_ENTITY_SELECTIONS
  );

  const primaryFilterCurrentInstance = filterMap.get(primaryFilterIdToSet);

  if (primaryFilterCurrentInstance) {
    const mutablePrimaryFilter = { ...primaryFilterCurrentInstance };

    if (mutablePrimaryFilter.type === FilterType.TREE) {
      const primaryTreeFilter = mutablePrimaryFilter;
      primaryTreeFilter.selections = defaultSelectionsForPrimary;
      primaryTreeFilter.isPrimary = true;
      primaryTreeFilter.primarySelections = defaultSelectionsForPrimary;
      filterMap.set(primaryFilterIdToSet, primaryTreeFilter);
    } else {
      mutablePrimaryFilter.isPrimary = true;
      filterMap.set(primaryFilterIdToSet, mutablePrimaryFilter);
    }
  }

  return filterMap;
};
